import { Component, EventEmitter, OnInit, Output, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AssistantChatFeature } from '@omni/ai/features/assistant-chat.feature';
import { NovaConversationEntry } from '@omni/ai/services/nova-conversation-index.service';

interface ConversationGroup {
  /** Translation key of the group heading. */
  labelKey: string;
  entries: NovaConversationEntry[];
}

@Component({
  selector: 'nova-conversation-list',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './nova-conversation-list.component.html',
  styleUrls: ['./nova-conversation-list.component.scss'],
})
export class NovaConversationListComponent implements OnInit {
  @Output() resume = new EventEmitter<NovaConversationEntry>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() newConversation = new EventEmitter<void>();
  @Output() deleteConversation = new EventEmitter<NovaConversationEntry>();

  readonly #assistant = inject(AssistantChatFeature);
  readonly #translate = inject(TranslateService);

  groups: ConversationGroup[] = [];
  loading = true;

  trackById(_index: number, entry: NovaConversationEntry): string { return entry.id; }
  trackByGroup(_i: number, g: ConversationGroup): string { return g.labelKey; }

  /** Delete a conversation: emit to the host (which deletes server-side + index)
   *  and drop it from the local list immediately for responsive feedback. */
  onDelete(event: Event, entry: NovaConversationEntry): void {
    event.stopPropagation();
    this.deleteConversation.emit(entry);
    this.groups = this.groups
      .map(g => ({ ...g, entries: g.entries.filter(e => e.id !== entry.id) }))
      .filter(g => g.entries.length);
  }

  async ngOnInit(): Promise<void> {
    try {
      const entries = await this.#assistant.listConversations();
      this.groups = this.#buildGroups(entries);
    } catch {
      this.groups = [];
    } finally {
      this.loading = false;
    }
  }

  /** Month and day of `iso` in the app language (e.g. "Oct 26", "26. Okt."); empty when `iso` is empty. */
  formatDate(iso: string): string {
    if (!iso) return '';
    const locale = (this.#translate.currentLang || 'en').replace('_', '-');
    try {
      return new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  #buildGroups(entries: NovaConversationEntry[]): ConversationGroup[] {
    const meetings: NovaConversationEntry[] = [];
    const contacts: NovaConversationEntry[] = [];
    const accounts: NovaConversationEntry[] = [];
    const spotlight: NovaConversationEntry[] = [];
    const general: NovaConversationEntry[] = [];

    for (const e of entries) {
      if (!e.origin) { general.push(e); continue; }
      switch (e.origin.kind) {
        case 'meeting':   meetings.push(e);  break;
        case 'contact':   contacts.push(e);  break;
        case 'account':   accounts.push(e);  break;
        case 'spotlight': spotlight.push(e); break;
        default:          general.push(e);   break;
      }
    }

    const result: ConversationGroup[] = [];
    if (meetings.length)  result.push({ labelKey: 'MEETINGS',          entries: meetings });
    if (contacts.length)  result.push({ labelKey: 'CONTACTS',          entries: contacts });
    if (accounts.length)  result.push({ labelKey: 'ACCOUNTS',          entries: accounts });
    if (spotlight.length) result.push({ labelKey: 'CONTACT_SPOTLIGHT', entries: spotlight });
    if (general.length)   result.push({ labelKey: 'GENERAL',           entries: general });
    return result;
  }
}
